'use client'
import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { RootState } from "store";

import { 
  Box, 
  CircularProgress, 
  Paper, 
  Typography, 
  Button,
  Stack
} from "@mui/material";
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon
} from "@mui/icons-material";

import ModalComp from "app/Components/Modals/ModalTemplate";
import TextEditor from "app/Components/Fields/TextEditor";

import { getNote, saveNote } from "_DatabaseFunctions/directConnect/users";
import { formatDate } from "utils/helperFunctions";

interface NotesModalProps {
  dets: {
    open?: boolean;
    itemName?: string;
    itemId?: string;
  };
}

interface Note {
  content: string;
  edittedOn: number;
}

function NotesModal({ dets }: NotesModalProps) {
  const { itemName = "", itemId = "" } = dets;
  const user = useSelector((state: RootState) => state.user.user);

  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [lastEdited, setLastEdited] = useState<string | null>(null);
  const [content, setContent] = useState<string>("");
  const [originalContent, setOriginalContent] = useState<string>("");

  const fetchNote = useCallback(async () => {
    if (!itemId || !user?.uid) return;
    
    setLoading(true);
    try {
      const note = await getNote({ userId: user.uid, itemId });
      if (note) {
        const noteContent = note.content || "";
        setContent(noteContent);
        setOriginalContent(noteContent);
        setLastEdited(formatDate(note.edittedOn));
      } else {
        setContent("");
        setOriginalContent("");
        setLastEdited(null);
      }
    } catch (err) {
      console.error("Error fetching notes:", err);
    } finally {
      setLoading(false);
    }
  }, [itemId, user?.uid]);

  useEffect(() => {
    setOpen(dets.open || false);
    if (dets.open && itemId) {
      fetchNote();
    } else {
      setLoading(false);
    }
  }, [dets.open, itemId, fetchNote]);

  const closeModal = useCallback(() => {
    setOpen(false);
    setIsEditing(false);
    setContent(originalContent); // Reset to original content on close
  }, [originalContent]);

  const handleSave = useCallback(async () => {
    if (!user?.uid || !itemId) return;
    
    try {
      await saveNote({
        userId: user.uid,
        itemId,
        itemName,
        content,
      });
      setOriginalContent(content);
      setLastEdited(formatDate(Date.now()));
      setIsEditing(false);
    } catch (err) {
      console.error("Error saving note:", err);
    }
  }, [user?.uid, itemId, itemName, content]);

  const handleCancel = useCallback(() => {
    setContent(originalContent);
    setIsEditing(false);
  }, [originalContent]);

  const handleEdit = useCallback(() => {
    setIsEditing(true);
  }, []);

  if (loading) {
    return (
      <ModalComp
        title={`${itemName} Notes`}
        modalType="info"
        msg=""
        show={open}
        onClose={closeModal}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </ModalComp>
    );
  }

  return (
    <ModalComp
      title={`${itemName} Notes`}
      modalType="info"
      msg=""
      show={open}
      onClose={closeModal}
      contentSx={{
        px: 0, // Remove horizontal padding
        py: 1, // Minimal vertical padding
        alignItems: 'stretch', // Allow content to fill width
        textAlign: 'left', // Left align for notes
      }}
    >
      <Box sx={{ width: '600px', maxWidth: '90vw' }}>
        {isEditing ? (
          <>
            <TextEditor
              text={content}
              handleChange={(value: string) => setContent(value)}
              placeholder="Add notes here..."
            />
            
            {lastEdited && (
              <Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{ px: 2, py: 1, display: 'block', fontStyle: 'italic' }}
              >
                Last edited: {lastEdited}
              </Typography>
            )}

            <Stack direction="row" spacing={2} sx={{ justifyContent: 'center', p: 2 }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                color="primary"
              >
                Save
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<CloseIcon />}
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </Stack>
          </>
        ) : (
          <>
            <Paper 
              sx={{ 
                p: 2, 
                minHeight: 300,
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'grey.200'
              }}
            >
              {content ? (
                <Box
                  sx={{ textAlign: "left" }}
                  dangerouslySetInnerHTML={{ __html: content }}
                />
              ) : (
                <Typography 
                  color="text.secondary" 
                  sx={{ fontStyle: 'italic', textAlign: 'center', py: 4 }}
                >
                  No notes for {itemName}. Click edit to add notes.
                </Typography>
              )}
            </Paper>

            {lastEdited && (
              <Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{ px: 2, py: 1, display: 'block', fontStyle: 'italic' }}
              >
                Last edited: {lastEdited}
              </Typography>
            )}

            <Stack direction="row" spacing={2} sx={{ justifyContent: 'center', p: 2 }}>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={handleEdit}
                color="primary"
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<CloseIcon />}
                onClick={closeModal}
              >
                Close
              </Button>
            </Stack>
          </>
        )}
      </Box>
    </ModalComp>
  );
}

export default NotesModal;
